/**
 * AdminPbiReportInstanceResults - Presentation component for PBI Report Instance results.
 *
 * This component handles different result rendering states for the instance management
 * interface including loading, error, empty, and data states. It composes the
 * PbiReportsInstanceTable and RsmPagination components when data is available.
 *
 * Follows the pattern established by AdminPbiReportResults.tsx for consistency.
 *
 * @module AdminSettings/AdminPbiReportInstanceResults
 */
import React from 'react'
import { CircularProgress, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { PbiReportInstanceViewModel } from '@rsmus/ecp-userservice'
import RsmPagination, {
  RsmPaginationPageSizes,
} from '../../rsmCoreComponents/components/RsmPagination/RsmPagination'
// eslint-disable-next-line import/no-named-as-default
import NoSearchFound from '../../rsmCoreComponents/components/NoSearchFound/NoSearchFound'
import { Styles } from '../../types'
import Unavailable from '../../pages/Unavailable/Unavailable'
import PbiReportsInstanceTable from './PbiReportsInstanceTable'

/**
 * Props interface for the AdminPbiReportInstanceResults component.
 * Contains data, loading state, pagination, sorting, and callback handlers.
 */
interface AdminPbiReportInstanceResultsProps {
  /** Object containing instance data array and total row count */
  instancesData: { data: PbiReportInstanceViewModel[]; totalRows: number }
  /** Flag indicating if an error occurred fetching data */
  isInstanceError: boolean
  /** Flag indicating if component is fully mounted and initial fetch complete */
  isMounted: boolean
  /** Current page number (1-based) */
  pageNumber: number
  /** Number of items per page */
  pageSize: RsmPaginationPageSizes
  /** Callback for handling page changes */
  onPageChange: (pageNumber: number, pageSize: RsmPaginationPageSizes) => void
  /** Currently sorted column identifier */
  sortColumn: string
  /** Current sort direction */
  sortDirection: 'asc' | 'desc'
  /** Callback for handling sort changes */
  onSorting: (columnName: string, direction: 'asc' | 'desc') => void
  /** Optional callback when user selects Edit action */
  onEditReportInstance?: (instance: PbiReportInstanceViewModel) => void
  /** Optional callback when user selects Remove action */
  onRemoveReportInstance?: (instance: PbiReportInstanceViewModel) => void
}

/**
 * Styles object for the results component.
 * Provides centered loading spinner styling.
 */
const styles: Styles = {
  container: () => ({
    display: 'flex',
    justifyContent: 'center',
    verticalAlign: 'middle',
    height: '2rem',
    marginTop: '2rem',
  }),
}

/**
 * AdminPbiReportInstanceResults - Renders the appropriate state for instance results.
 *
 * States:
 * 1. Error state - Shows Unavailable component
 * 2. Empty state - Shows NoSearchFound component
 * 3. Data state - Shows PbiReportsInstanceTable with pagination
 * 4. Loading state - Shows CircularProgress spinner
 *
 * @param props - Component props
 * @returns A React component rendering the appropriate state
 */
const AdminPbiReportInstanceResults = ({
  instancesData,
  isInstanceError,
  isMounted,
  pageNumber,
  pageSize,
  onPageChange,
  sortColumn,
  sortDirection,
  onSorting,
  onEditReportInstance,
  onRemoveReportInstance,
}: AdminPbiReportInstanceResultsProps) => {
  const { t } = useTranslation()

  // Error state: show Unavailable component
  if (isInstanceError) {
    return <Unavailable />
  }

  // Empty state: show NoSearchFound when mounted but no data
  if (isMounted && (!instancesData || instancesData.data.length === 0)) {
    return (
      <NoSearchFound
        title={t('AdminSettingsForm.PbiInstanceColumns.NoInstances')}
        subTitle=""
      />
    )
  }

  // Data state: show table with pagination when data exists
  if (instancesData && instancesData.data && instancesData.data.length > 0) {
    return (
      <>
        <PbiReportsInstanceTable
          instancesData={instancesData}
          onSort={onSorting}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          isMounted={isMounted}
          onEditInstance={onEditReportInstance}
          onRemoveInstance={onRemoveReportInstance}
        />
        <RsmPagination
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalRows={instancesData?.totalRows ?? 0}
          onPageChange={onPageChange}
        />
      </>
    )
  }

  // Loading state: show centered spinner
  return (
    <Box sx={styles.container} aria-busy="true">
      <CircularProgress
        color="secondary"
        data-testid="Spn_ReportInstances_Loading"
      />
    </Box>
  )
}

export default AdminPbiReportInstanceResults
